import { SigninRedirectArgs, SignoutRedirectArgs } from 'oidc-client-ts'
import { AuthContextProps, useAuth as oidcAuth } from 'oidc-react'

import { LogoutManager } from '../../utils'
import { removeMetadataCacheKeys } from './useMetadataCache'

/**
 * Interface representing the return type of an authentication hook.
 */
export interface UseAuthReturn {
  /**
   * The current authentication context or state.
   * This can be an object containing authentication details or undefined if no authentication context exists.
   */
  auth: AuthContextProps | undefined

  /**
   * Function to initiate the logout process.
   * This function takes no parameters and does not return a value.
   */
  onLogout: (args?: SignoutRedirectArgs, sendLogoutMessage?: boolean) => Promise<void>

  /**
   * Function to initiate the sign-in process.
   * This function may take an optional `args` parameter of type `SigninRedirectArgs` to provide additional sign-in arguments.
   * It does not return a value.
   */
  signIn: (args?: SigninRedirectArgs) => Promise<void>

  /**
   * Function to sign out the current user.
   * Similar to `onLogout`, it takes no parameters and does not return a value.
   * The distinction between `signOut` and `onLogout` should be defined based on their specific use cases within the application.
   */
  signOut: (sendLogoutMessage?: boolean) => Promise<void>

  removeUser: (revokeTokens?: boolean, sendLogoutMessage?: boolean) => Promise<void>
}

/**
 * The `useAuth` function in TypeScript manages authentication state and provides functions for signing
 * in, signing out, and redirecting on logout.
 * @returns The `useAuth` function returns an object with the following properties:
 * - `auth`
 * - `signOut`
 * - `signIn`
 * - `onLogout`
 */
export function useAuth(): UseAuthReturn {
  const auth: AuthContextProps = oidcAuth()

  /**
   * The function `signIn` asynchronously signs in a user with optional arguments for redirection.
   * @param {SigninRedirectArgs} [args] - The `args` parameter in the `signIn` function is of type
   * `SigninRedirectArgs`, which is an optional parameter. This means that you can call the `signIn`
   * function with or without providing any arguments. If you provide arguments, they should be of type
   * `SigninRedirectArgs`.
   */
  async function signIn(args?: SigninRedirectArgs) {
    await auth?.signIn(args)
  }

  /**
   * The `signOut` function asynchronously signs the user out
   */
  async function signOut(sendLogoutMessage = true) {
    removeMetadataCacheKeys()

    if (sendLogoutMessage) {
      const logoutManager = (window as any).LogoutManager as LogoutManager
      logoutManager.sendLogoutMessage()
    }

    await auth.userManager.removeUser()
    await auth?.signOut()
  }

  /**
   * The `onLogout` function asynchronously signs out the user with a redirect using the `auth` object.
   * @param args additional signout args to be passed to the redirect
   * @param sendLogoutMessage set to true to signal other tabs that a logout has been initiated
   */
  const onLogout = async (args?: SignoutRedirectArgs, sendLogoutMessage = true) => {
    removeMetadataCacheKeys()

    if (sendLogoutMessage) {
      const logoutManager = (window as any).LogoutManager as LogoutManager
      logoutManager.sendLogoutMessage()
    }

    await auth?.signOutRedirect(args)
  }

  const removeUser = async (revokeTokens = true, sendLogoutMessage = false) => {
    if (revokeTokens) await auth.userManager.revokeTokens()

    if (sendLogoutMessage) {
      const logoutManager = (window as any).LogoutManager as LogoutManager
      logoutManager.sendLogoutMessage()
    }

    await auth.userManager.removeUser()
  }

  return { auth, signOut, signIn, onLogout, removeUser }
}
